import React, { useState, useEffect, useRef } from 'react';

const RefinedPoemTree = () => {
  const [treeData, setTreeData] = useState(null);
  const [activeBranch, setActiveBranch] = useState(null);
  const [visibleNodes, setVisibleNodes] = useState(new Set(['root']));
  const [currentPath, setCurrentPath] = useState([]);
  const [poemVisible, setPoemVisible] = useState(false);
  const treeRef = useRef(null);
  const activeNodeRef = useRef(null);
  
  useEffect(() => {
    // Load poem data
    fetch('/poemData.json')
      .then(response => response.json())
      .then(data => setTreeData(data))
      .catch(error => console.error("Error loading poem data:", error));
  }, []);
  
  useEffect(() => {
    // Animate poem appearance when path changes
    if (currentPath.length > 0) {
      // Small delay before showing poem to allow for animation
      const timer = setTimeout(() => {
        setPoemVisible(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setPoemVisible(false);
    }
  }, [currentPath]);
  
  useEffect(() => {
    // Scroll to active node when it changes, but only if it's not the root node
    if (activeNodeRef.current && activeBranch && activeBranch.length > 1 && activeBranch[0] === 'root') {
      // Don't scroll for root node
      if (activeBranch.length > 1 && activeBranch[activeBranch.length - 1] !== 'root') {
        activeNodeRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        });
      }
    }
  }, [activeBranch]);
  
  // Handle node click - reveal children of clicked node
  const handleNodeClick = (nodeId, children) => {
    // Temporarily hide poem during transitions
    setPoemVisible(false);
    
    const newVisibleNodes = new Set(visibleNodes);
    
    // If this node has children, reveal them
    if (children && children.length > 0) {
      children.forEach(child => newVisibleNodes.add(child.id));
      setVisibleNodes(newVisibleNodes);
    }
    
    // Update the active branch for highlighting
    const path = findPathToNode(nodeId, treeData);
    
    // Update current path display
    if (path) {
      setCurrentPath(path);
    }
    
    // If clicking the same node twice, toggle the highlight off
    if (activeBranch && activeBranch[activeBranch.length - 1] === nodeId) {
      setActiveBranch(null);
    } else {
      setActiveBranch(path);
    }
  };
  
  // Navigate to next visible siblings
  const navigateNext = () => {
    if (!activeBranch || activeBranch.length === 0) return;
    
    // Get the currently active node ID
    const currentNodeId = activeBranch[activeBranch.length - 1];
    
    // Find the parent node of the current node
    const parentPath = activeBranch.slice(0, -1);
    let parentNode = treeData;
    
    // Navigate to the parent node
    for (let i = 1; i < parentPath.length; i++) {
      const childId = parentPath[i];
      parentNode = parentNode.children.find(child => child.id === childId);
    }
    
    if (!parentNode || !parentNode.children) return;
    
    // Find the index of the current node in the parent's children
    const currentIndex = parentNode.children.findIndex(child => child.id === currentNodeId);
    
    // If found and there is a next sibling, navigate to it
    if (currentIndex !== -1 && currentIndex < parentNode.children.length - 1) {
      const nextNode = parentNode.children[currentIndex + 1];
      handleNodeClick(nextNode.id, nextNode.children);
    }
  };
  
  // Navigate to previous visible siblings
  const navigatePrev = () => {
    if (!activeBranch || activeBranch.length === 0) return;
    
    // Get the currently active node ID
    const currentNodeId = activeBranch[activeBranch.length - 1];
    
    // Find the parent node of the current node
    const parentPath = activeBranch.slice(0, -1);
    let parentNode = treeData;
    
    // Navigate to the parent node
    for (let i = 1; i < parentPath.length; i++) {
      const childId = parentPath[i];
      parentNode = parentNode.children.find(child => child.id === childId);
    }
    
    if (!parentNode || !parentNode.children) return;
    
    // Find the index of the current node in the parent's children
    const currentIndex = parentNode.children.findIndex(child => child.id === currentNodeId);
    
    // If found and there is a previous sibling, navigate to it
    if (currentIndex > 0) {
      const prevNode = parentNode.children[currentIndex - 1];
      handleNodeClick(prevNode.id, prevNode.children);
    }
  };
  
  // Reset the tree to only show the root
  const resetTree = () => {
    setPoemVisible(false);
    setVisibleNodes(new Set(['root']));
    setActiveBranch(null);
    setCurrentPath([]);
  };
  
  // Find parent node of a given node
  const findParentInTree = (nodeId, tree) => {
    if (!tree || !tree.children) return false;
    
    // Check direct children
    for (const child of tree.children || []) {
      if (child.id === nodeId) {
        // Found the node as a direct child, check if any siblings are active
        return tree.children.some(sibling => 
          sibling.id !== nodeId && isNodeActive(sibling.id)
        );
      }
    }
    
    // Recursively check in children
    for (const child of tree.children) {
      const foundInChild = findParentInTree(nodeId, child);
      if (foundInChild) return true;
    }
    
    return false;
  };
  
  // Function to get path from root to a specific node
  const findPathToNode = (nodeId, tree, currentPath = []) => {
    if (!tree) return null;
    
    // Add current node to path
    const newPath = [...currentPath, tree.id];
    
    // If this is the target node, return the path
    if (tree.id === nodeId) {
      return newPath;
    }
    
    // Search in children
    if (tree.children) {
      for (const child of tree.children) {
        const pathFound = findPathToNode(nodeId, child, newPath);
        if (pathFound) return pathFound;
      }
    }
    
    // Not found in this branch
    return null;
  };
  
  const isNodeActive = (nodeId) => {
    if (!activeBranch) return false;
    
    // Get the index of this node in the active branch path
    const nodeIndex = activeBranch.indexOf(nodeId);
    
    // Only highlight if this node is part of the direct path to the selected node
    // (not any sibling branches)
    return nodeIndex !== -1;
  };
  
  const isNodeVisible = (nodeId) => {
    return visibleNodes.has(nodeId);
  };
  
  // Check if a connector should be highlighted
  const isConnectorActive = (nodeId, childId) => {
    if (!activeBranch) return false;
    
    const nodeIndex = activeBranch.indexOf(nodeId);
    const childIndex = activeBranch.indexOf(childId);
    
    // The connector is active if both the parent and child are consecutive in the active branch
    return nodeIndex !== -1 && childIndex !== -1 && childIndex === nodeIndex + 1;
  };
  
  // Recursive function to render a node and its children
  const renderNode = (node, level = 0) => {
    if (!node) return null;
    
    const isActive = isNodeActive(node.id);
    const isVisible = isNodeVisible(node.id);
    const isLeafNode = !node.children || node.children.length === 0;
    
    // Don't render if the node is not visible
    if (!isVisible) return null;
    
    // Calculate opacity - check if there's an active branch and this node isn't in it
    const shouldBeGreyedOut = activeBranch && 
                             !isActive && 
                             // Only apply to siblings of active nodes, not to newly revealed options
                             node.id !== 'root' &&
                             // Find parent to check if any siblings are active
                             findParentInTree(node.id, treeData);
    
    // Check if this is the last active node (current selection)
    const isLastActive = activeBranch && activeBranch[activeBranch.length - 1] === node.id;
    
    return (
      <div className="node-wrapper">
        <div 
          className={`tree-node level-${level} ${isActive ? 'active' : ''} ${isLeafNode ? 'leaf-node' : ''} ${isLastActive ? 'last-active' : ''}`}
          onClick={() => handleNodeClick(node.id, node.children)}
          style={{ opacity: shouldBeGreyedOut ? 0.65 : 1 }}
          ref={isLastActive ? activeNodeRef : null}
        >
          <div className="node-content">
            {node.text}
            {node.badge && <span className="badge">{node.badge}</span>}
            {isLeafNode && <span className="end-marker">∎</span>}
          </div>
          
          {node.children && node.children.length > 0 && node.children.some(child => isNodeVisible(child.id)) && (
            <div className={`connector h-connector ${
              node.children.some(child => isConnectorActive(node.id, child.id)) ? 'active-connector' : ''
            }`}></div>
          )}
        </div>
        
        {node.children && node.children.length > 0 && (
          <div className="children-container">
            {node.children.map(child => (
              <div key={child.id} className={`child-branch ${isConnectorActive(node.id, child.id) ? 'active-branch' : ''}`}>
                {renderNode(child, level + 1)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };
  
  // Function to find node text by ID
  const findNodeById = (node, id) => {
    if (!node) return null;
    if (node.id === id) return node.text;
    if (node.children) {
      for (const child of node.children) {
        const found = findNodeById(child, id);
        if (found) return found;
      }
    }
    return null;
  };
  
  if (!treeData) {
    return <div className="loading">Loading verse architecture...</div>;
  }
  
  return (
    <div className="poem-tree-container">
      <h1>Multi Miss Muffet</h1>
      <p className="subtitle">Follow the branches to reveal the poetic journey</p>
      
      <div className="main-content">
        <div className="left-panel">
          <div className="controls">
            <button className="reset-button" onClick={resetTree}>
              Return to Root
            </button>
          </div>
          
          <div className="tree-visualization" ref={treeRef}>
            {renderNode(treeData)}
          </div>
        </div>
        
        <div className={`right-panel ${poemVisible ? 'visible' : ''}`}>
          {currentPath.length > 0 && (
            <div className="poem-display">
              <h2>Your Poem Path</h2>
              <div className="poem-content">
                {currentPath.map((nodeId) => {
                  // Find node text from tree
                  let nodeText = "";
                  if (nodeId === "root" && treeData) {
                    nodeText = treeData.text;
                  } else {
                    const foundText = findNodeById(treeData, nodeId);
                    if (foundText) nodeText = foundText;
                  }
                  
                  return nodeText ? (
                    <p key={nodeId} className="poem-line">
                      {nodeText}
                    </p>
                  ) : null;
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=Montserrat:wght@300;400;500&display=swap');
        
        .poem-tree-container {
          font-family: 'Montserrat', sans-serif;
          background-color: #f9f8f5;
          color: #2c2c2c;
          padding: 40px;
          min-height: 80vh;
          max-width: 100%;
          margin: 0 auto;
        }
        
        h1 {
          text-align: center;
          font-family: 'Cormorant Garamond', serif;
          font-weight: 300;
          color: #2a2a2a;
          margin-bottom: 12px;
          font-size: 2.5rem;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }
        
        .subtitle {
          text-align: center;
          margin-bottom: 30px;
          color: #605e5c;
          font-weight: 300;
          font-size: 0.95rem;
          letter-spacing: 0.5px;
        }
        
        /* Two-column layout for desktop */
        .main-content {
          display: flex;
          gap: 40px;
          position: relative;
        }
        
        .left-panel {
          flex: 1;
          min-width: 0;
        }
        
        .right-panel {
          width: 350px;
          opacity: 0;
          transform: translateX(20px);
          transition: all 0.7s cubic-bezier(0.19, 1, 0.22, 1);
          position: sticky;
          top: 40px;
          align-self: flex-start;
          max-height: calc(100vh - 80px);
          overflow-y: auto;
        }
        
        .right-panel.visible {
          opacity: 1;
          transform: translateX(0);
        }
        
        .controls {
          margin-bottom: 30px;
          text-align: center;
        }
        
        .reset-button {
          background: transparent;
          color: #3a3a3a;
          border: 1px solid #3a3a3a;
          padding: 8px 24px;
          font-family: 'Montserrat', sans-serif;
          font-weight: 300;
          font-size: 0.85rem;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .reset-button:hover {
          background: #3a3a3a;
          color: #f8f6f1;
        }
        
        /* Poem display styling - more subtle and elegant now */
        .poem-display {
          background-color: transparent;
          padding: 0;
          font-family: 'Cormorant Garamond', serif;
          border-left: 1px solid rgba(0,0,0,0.08);
          padding-left: 25px;
        }
        
        .poem-display h2 {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 300;
          font-size: 1.4rem;
          color: #2a2a2a;
          margin-bottom: 30px;
          letter-spacing: 1px;
          text-transform: uppercase;
          opacity: 0.85;
        }
        
        .poem-content {
          line-height: 1.8;
        }
        
        .poem-line {
          margin-bottom: 1.4em;
          font-size: 1.1rem;
          color: #3c3c3c;
          line-height: 1.6;
          font-weight: 300;
          transition: all 0.5s ease;
          position: relative;
          padding-bottom: 0.6em;
          animation: fadeInLine 1.2s cubic-bezier(0.19, 1, 0.22, 1);
          animation-fill-mode: both;
        }
        
        @keyframes fadeInLine {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .poem-line:not(:last-child):after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          width: 20px;
          height: 1px;
          background-color: rgba(141, 127, 108, 0.2);
        }
        
        .poem-line:last-child {
          position: relative;
          padding-bottom: 0.5em;
        }
        
        .poem-line:last-child:after {
          content: "∎";
          position: relative;
          display: inline-block;
          font-size: 0.8rem;
          color: #8d7f6c;
          opacity: 0.5;
          margin-left: 0.5em;
        }
        
        .loading {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 300px;
          font-size: 1rem;
          color: #605e5c;
          font-family: 'Cormorant Garamond', serif;
          letter-spacing: 1px;
        }
        
        .tree-visualization {
          overflow-x: auto;
          padding: 30px 10px 30px 0;
          background-color: transparent;
          min-height: 400px;
          scrollbar-width: thin;
          scrollbar-color: #d3d1c7 transparent;
        }
        
        .tree-visualization::-webkit-scrollbar {
          height: 6px;
        }
        
        .tree-visualization::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .tree-visualization::-webkit-scrollbar-thumb {
          background-color: #d3d1c7;
          border-radius: 10px;
        }
        
        .node-wrapper {
          display: flex;
          flex-direction: row;
          align-items: center;
        }
        
        .tree-node {
          display: flex;
          flex-direction: row;
          align-items: center;
          position: relative;
          transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
          margin: 14px 0;
          z-index: 2;
          cursor: pointer;
          opacity: 1; /* Default full opacity */
        }
        
        .node-content {
          padding: 14px 22px;
          border-radius: 0;
          transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
          position: relative;
          background-color: rgba(251, 250, 246, 0.7);
          min-width: 180px;
          font-family: 'Cormorant Garamond', serif;
          line-height: 1.4;
          border-left: 2px solid rgba(141, 127, 108, 0.5);
          box-shadow: 0 1px 2px rgba(0,0,0,0.02);
          font-size: 1rem;
          color: #3c3c3c;
        }
        
        /* Hover styles revised for sophistication */
        .tree-node:hover .node-content {
          transform: translateY(-2px);
          box-shadow: 0 3px 8px rgba(0,0,0,0.03);
          border-left-color: rgba(141, 127, 108, 0.8);
        }
        
        /* Different levels now maintain consistent styling with subtle differences */
        .level-0 .node-content {
          background: rgba(42, 42, 42, 0.9);
          color: #f8f6f1;
          font-weight: 400;
          font-size: 1.1rem;
          border-left: 2px solid rgba(141, 127, 108, 0.8);
          letter-spacing: 0.5px;
        }
        
        .level-1 .node-content {
          border-left: 2px solid rgba(141, 127, 108, 0.5);
          font-size: 1.05rem;
          font-weight: 400;
        }
        
        .level-2 .node-content {
          border-left: 2px solid rgba(141, 127, 108, 0.5);
          font-weight: 400;
        }
        
        .level-3 .node-content {
          border-left: 2px solid rgba(141, 127, 108, 0.5);
          font-weight: 400;
        }
        
        .level-4 .node-content {
          border-left: 2px solid rgba(141, 127, 108, 0.5);
          font-weight: 400;
        }
        
        .connector {
          position: relative;
        }
        
        .h-connector {
          height: 1px;
          background-color: rgba(213, 208, 195, 0.6);
          width: 60px;
          margin: 0 8px;
        }
        
        .children-container {
          display: flex;
          flex-direction: column;
          position: relative;
          justify-content: center;
          min-height: 100%;
        }
        
        .child-branch {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .child-branch:before {
          content: '';
          position: absolute;
          left: -30px;
          top: 0;
          bottom: 0;
          width: 1px;
          background: rgba(213, 208, 195, 0.6);
        }
        
        /* Active node styles - more refined now */
        .tree-node.active {
          opacity: 1;
        }
        
        .tree-node.active .node-content {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          z-index: 5;
          border-left-width: 3px;
        }
        
        /* Last active node (current selection) */
        .tree-node.last-active .node-content {
          border-left-width: 3px;
          border-left-color: rgba(141, 127, 108, 1);
          box-shadow: 0 4px 14px rgba(0,0,0,0.07);
        }
        
        .level-0.active .node-content {
          background: #262626;
        }
        
        /* All active states now share common styling */
        .active .node-content {
          background-color: rgba(246, 243, 236, 0.9);
        }
        
        .active-connector {
          background-color: rgba(141, 127, 108, 0.8) !important;
          z-index: 4 !important;
        }
        
        .active-branch:before {
          background: rgba(141, 127, 108, 0.8) !important;
        }
        
        .badge {
          display: inline-block;
          background-color: rgba(141, 127, 108, 0.1);
          border-radius: 0;
          padding: 2px 6px;
          font-size: 0.7rem;
          margin-left: 8px;
          font-family: 'Montserrat', sans-serif;
          letter-spacing: 0.5px;
          vertical-align: middle;
          color: rgba(105, 94, 82, 0.8);
        }
        
        /* End node styling */
        .leaf-node {
          cursor: default;
        }
        
        .leaf-node .node-content {
          border-right: 2px solid rgba(141, 127, 108, 0.5);
          background-color: rgba(249, 247, 243, 0.6);
          box-shadow: 0 1px 2px rgba(0,0,0,0.01);
        }
        
        .leaf-node:hover .node-content {
          transform: none;
          box-shadow: 0 1px 2px rgba(0,0,0,0.02);
        }
        
        .end-marker {
          display: inline-block;
          margin-left: 10px;
          color: rgba(141, 127, 108, 0.7);
          font-size: 0.8rem;
          opacity: 0.7;
        }
        
        /* Entrance animation for newly revealed nodes */
        .tree-node {
          animation: fadeIn 0.8s cubic-bezier(0.19, 1, 0.22, 1);
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Responsive design for smaller screens */
        @media (max-width: 1024px) {
          .main-content {
            flex-direction: column;
          }
          
          .right-panel {
            width: 100%;
            margin-top: 40px;
            position: static;
            transition: opacity 0.5s ease;
          }
          
          .poem-display {
            border-left: none;
            border-top: 1px solid rgba(0,0,0,0.08);
            padding-left: 0;
            padding-top: 30px;
          }
        }
        
        @media (max-width: 768px) {
          .poem-tree-container {
            padding: 20px;
          }
          
          h1 {
            font-size: 2rem;
          }
          
          .node-content {
            padding: 10px 16px;
            min-width: 150px;
            font-size: 0.9rem;
          }
          
          .h-connector {
            width: 40px;
          }
          
          .poem-display h2 {
            font-size: 1.3rem;
            margin-bottom: 20px;
          }
          
          .poem-line {
            font-size: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default RefinedPoemTree;